import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { scrollToId, LOGO_URL, BG_URL } from "../content";

export const Nav = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 500);
    return () => clearTimeout(t);
  }, []);

  return (
    <motion.nav
      data-testid="floating-nav"
      initial={{ opacity: 0, y: -16 }}
      animate={visible ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-5 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-1 sm:gap-2 pl-3 pr-1.5 py-1.5 rounded-full backdrop-blur-xl bg-[#070B11]/75 border border-white/10"
    >
      <img src={LOGO_URL} alt="Great Western Funding" className="brand-logo h-7 w-auto hidden sm:block" data-testid="nav-logo" />
      {[["LEARN", "learn"], ["HOST", "host"], ["FAQ", "faq"]].map(([label, id]) => (
        <button
          key={id}
          data-testid={`nav-${id}-link`}
          onClick={() => scrollToId(id)}
          className="font-mono-c text-[10px] sm:text-[11px] tracking-[0.15em] sm:tracking-[0.2em] text-white/60 hover:text-[#5D8FC4] transition-colors duration-200 px-1.5 sm:px-3 py-2"
        >
          {label}
        </button>
      ))}
      <button
        data-testid="nav-register-cta"
        onClick={() => scrollToId("register")}
        className="flex items-center gap-1 bg-[#5D8FC4] text-[#070B11] font-bold text-[10px] sm:text-[11px] tracking-[0.12em] sm:tracking-[0.15em] rounded-full px-3 sm:px-4 py-2.5 hover:bg-[#7FAEDD] transition-colors duration-200"
      >
        REGISTER <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={3} />
      </button>
    </motion.nav>
  );
};

export const CustomCursor = () => {
  useEffect(() => {
    if (!window.matchMedia("(pointer: fine)").matches) return;
    const dot = document.createElement("div");
    dot.className = "custom-cursor";
    dot.style.opacity = "0";
    document.body.appendChild(dot);
    const move = (e) => {
      dot.style.opacity = "1";
      dot.style.left = `${e.clientX}px`;
      dot.style.top = `${e.clientY}px`;
      const t = e.target.closest("button, a, input, select, label");
      dot.classList.toggle("is-hovering", !!t);
    };
    window.addEventListener("mousemove", move);
    return () => {
      window.removeEventListener("mousemove", move);
      dot.remove();
    };
  }, []);
  return null;
};

export const MobileStickyCTA = () => (
  <div className="fixed bottom-0 left-0 right-0 z-[55] md:hidden backdrop-blur-xl bg-[#070B11]/85 border-t border-white/10 p-3" data-testid="mobile-sticky-cta">
    <button onClick={() => scrollToId("register")} className="btn-accent w-full text-sm" data-testid="mobile-sticky-cta-button">
      ✓ Reserve My Free Seat
    </button>
  </div>
);

const WEBINAR_TARGET = new Date("2026-09-10T18:00:00-04:00").getTime();

function getTimeLeft() {
  const diff = Math.max(0, WEBINAR_TARGET - Date.now());
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor(diff / 3600000) % 24,
    mins: Math.floor(diff / 60000) % 60,
    secs: Math.floor(diff / 1000) % 60,
  };
}

export const Countdown = () => {
  const [t, setT] = useState(getTimeLeft);
  useEffect(() => {
    const id = setInterval(() => setT(getTimeLeft()), 1000);
    return () => clearInterval(id);
  }, []);
  const units = [["DAYS", t.days], ["HRS", t.hours], ["MIN", t.mins], ["SEC", t.secs]];
  return (
    <div className="flex items-center gap-3" data-testid="hero-countdown">
      {units.map(([label, value], i) => (
        <div key={label} className="flex items-center gap-3">
          <div className="text-center border border-white/10 bg-white/[0.03] backdrop-blur-sm px-3 py-2 min-w-[64px]">
            <span className="block font-mono-c text-2xl font-bold text-[#5D8FC4] tabular-nums" data-testid={`countdown-${label.toLowerCase()}`}>
              {String(value).padStart(2, "0")}
            </span>
            <span className="block font-mono-c text-[9px] tracking-[0.3em] text-white/35 mt-1">{label}</span>
          </div>
          {i < 3 && <span className="text-[#5D8FC4]/50 font-mono-c text-lg">:</span>}
        </div>
      ))}
    </div>
  );
};

export const Reveal = ({ children, delay = 0, className }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

export const PortalIntro = () => {
  const [gone, setGone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setGone(true), 1700);
    return () => clearTimeout(t);
  }, []);
  if (gone) return null;
  return (
    <div className="portal-intro" data-testid="portal-intro">
      <div className="portal-intro-ring" />
    </div>
  );
};

export const ParallaxBg = ({ opacity = 0.1, src = BG_URL }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-9%", "9%"]);
  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      <motion.img src={src} alt="" style={{ y, opacity }} className="w-full h-[118%] object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, #070B11 0%, transparent 30%, transparent 70%, #070B11 100%)" }} />
    </div>
  );
};

export const SectionLabel = ({ num, text }) => (
  <p className="font-mono-c text-[11px] tracking-[0.35em] text-[#5D8FC4] uppercase">
    [{num}] — {text}
  </p>
);
